import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Edit, ChevronLeft, Search } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { psAPI, controllingAPI, companyAPI } from '../../../services/api';
import { toast } from 'sonner';

const ProjectProfiles = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('projectprofiles');
  const [viewItem, setViewItem] = useState(null);
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  
  // Lookup data
  const [controllingAreas, setControllingAreas] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);

  const [formData, setFormData] = useState({
    profile_id: '',
    name: '',
    description: '',
    project_type: 'INTERNAL',
    controlling_area_id: '',
    company_code_id: '',
    profit_center_id: '',
    planning_method: 'MANUAL',
    budget_distribution: 'TOP_DOWN',
    scheduling_type: 'FORWARD',
    wbs_scheduling: true,
    network_scheduling: true,
    settlement_profile: true,
    default_settlement_rule: '',
    is_statistical: false,
    allow_actual_postings: true,
    allow_commitment_postings: true,
    allow_revenue_postings: false,
    allow_network: true,
    allow_milestones: true,
    status: 'Active'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [profilesRes, caRes, ccRes, pcRes] = await Promise.all([
        psAPI.getProjectProfiles(),
        controllingAPI.getControllingAreas(),
        companyAPI.getCompanyCodes(),
        controllingAPI.getProfitCenters()
      ]);
      setProfiles(profilesRes.data);
      setControllingAreas(caRes.data);
      setCompanyCodes(ccRes.data);
      setProfitCenters(pcRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditMode(false);
    setSelectedProfile(null);
    setFormData({
      profile_id: '',
      name: '',
      description: '',
      project_type: 'INTERNAL',
      controlling_area_id: '',
      company_code_id: '',
      profit_center_id: '',
      planning_method: 'MANUAL',
      budget_distribution: 'TOP_DOWN',
      scheduling_type: 'FORWARD',
      wbs_scheduling: true,
      network_scheduling: true,
      settlement_profile: true,
      default_settlement_rule: '',
      is_statistical: false,
      allow_actual_postings: true,
      allow_commitment_postings: true,
      allow_revenue_postings: false,
      allow_network: true,
      allow_milestones: true,
      status: 'Active'
    });
    setShowDialog(true);
  };

  const handleEdit = (profile) => {
    setEditMode(true);
    setSelectedProfile(profile);
    setFormData({
      profile_id: profile.profile_id,
      name: profile.name,
      description: profile.description || '',
      project_type: profile.project_type,
      controlling_area_id: profile.controlling_area_id,
      company_code_id: profile.company_code_id || '',
      profit_center_id: profile.profit_center_id || '',
      planning_method: profile.planning_method,
      budget_distribution: profile.budget_distribution,
      scheduling_type: profile.scheduling_type,
      wbs_scheduling: profile.wbs_scheduling ?? true,
      network_scheduling: profile.network_scheduling ?? true,
      settlement_profile: profile.settlement_profile ?? true,
      default_settlement_rule: profile.default_settlement_rule || '',
      is_statistical: profile.is_statistical,
      allow_actual_postings: profile.allow_actual_postings,
      allow_commitment_postings: profile.allow_commitment_postings,
      allow_revenue_postings: profile.allow_revenue_postings,
      allow_network: profile.allow_network,
      allow_milestones: profile.allow_milestones,
      status: profile.status
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.profile_id || !formData.name || !formData.controlling_area_id) {
        toast.error('Please fill in all required fields');
        return;
      }

      if (editMode) {
        await psAPI.updateProjectProfile(formData.profile_id, formData);
        toast.success('Profile updated successfully');
      } else {
        await psAPI.createProjectProfile(formData);
        toast.success('Profile created successfully');
      }
      
      setShowDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
  };

  const filteredProfiles = profiles.filter(p => 
    p.profile_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const projectTypes = [
    { value: 'INTERNAL', label: 'Internal Project' },
    { value: 'CUSTOMER', label: 'Customer Project' },
    { value: 'INVESTMENT', label: 'Investment Project' },
    { value: 'MAINTENANCE', label: 'Maintenance Project' },
    { value: 'RD', label: 'R&D Project' }
  ];

  const planningMethods = [
    { value: 'MANUAL', label: 'Manual' },
    { value: 'UNIT_COSTING', label: 'Unit Costing' },
    { value: 'ECP', label: 'Easy Cost Planning' },
    { value: 'NETWORK_COSTING', label: 'Network Costing' }
  ];

  const budgetDistributions = [
    { value: 'TOP_DOWN', label: 'Top-Down' },
    { value: 'BOTTOM_UP', label: 'Bottom-Up' },
    { value: 'MANUAL', label: 'Manual' }
  ];

  const schedulingTypes = [
    { value: 'FORWARD', label: 'Forward' },
    { value: 'BACKWARD', label: 'Backward' },
    { value: 'TODAY', label: 'From Today' }
  ];

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="project-profiles-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/ps')}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Project Profiles</h1>
          <p className="text-gray-500">Define project behavior and organizational defaults</p>
        </div>
        {writable && (<Button onClick={handleCreate} data-testid="create-profile-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Profile
        </Button>)}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search profiles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Profile ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Project Type</TableHead>
                <TableHead>Controlling Area</TableHead>
                <TableHead>Planning Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredProfiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No profiles found
                  </TableCell>
                </TableRow>
              ) : (
                filteredProfiles.map((profile) => (
                  <TableRow key={profile.profile_id} data-testid={`profile-row-${profile.profile_id}`}>
                    <TableCell className="font-medium">{profile.profile_id}</TableCell>
                    <TableCell>{profile.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{profile.project_type}</Badge>
                    </TableCell>
                    <TableCell>{profile.controlling_area_name || profile.controlling_area_id}</TableCell>
                    <TableCell>{profile.planning_method}</TableCell>
                    <TableCell>
                      <Badge variant={profile.status === 'Active' ? 'default' : 'secondary'}>
                        {profile.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <RecordActions doctype="projectprofiles" onView={() => setViewItem(profile)} onEdit={() => handleEdit(profile)} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Project Profile' : 'Create Project Profile'}</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Profile ID *</Label>
              <Input
                value={formData.profile_id}
                onChange={(e) => setFormData({...formData, profile_id: e.target.value.toUpperCase()})}
                placeholder="e.g., PROJ_INT"
                disabled={editMode}
                data-testid="profile-id-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Profile name"
                data-testid="name-input"
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Profile description"
              />
            </div>

            <div className="space-y-2">
              <Label>Project Type *</Label>
              <Select 
                value={formData.project_type} 
                onValueChange={(v) => setFormData({...formData, project_type: v})}
              >
                <SelectTrigger data-testid="project-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {projectTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Controlling Area *</Label>
              <Select 
                value={formData.controlling_area_id} 
                onValueChange={(v) => setFormData({...formData, controlling_area_id: v})}
              >
                <SelectTrigger data-testid="controlling-area-select">
                  <SelectValue placeholder="Select controlling area" />
                </SelectTrigger>
                <SelectContent>
                  {controllingAreas.map(ca => (
                    <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                      {ca.controlling_area_id} - {ca.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Company Code</Label>
              <Select 
                value={formData.company_code_id || "__none__"} 
                onValueChange={(v) => setFormData({...formData, company_code_id: v === "__none__" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company code" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {companyCodes.map(cc => (
                    <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                      {cc.company_code_id} - {cc.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Profit Center</Label>
              <Select 
                value={formData.profit_center_id || "__none__"} 
                onValueChange={(v) => setFormData({...formData, profit_center_id: v === "__none__" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select profit center" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {profitCenters.map(pc => (
                    <SelectItem key={pc.profit_center_id} value={pc.profit_center_id}>
                      {pc.profit_center_id} - {pc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Planning Method</Label>
              <Select 
                value={formData.planning_method} 
                onValueChange={(v) => setFormData({...formData, planning_method: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {planningMethods.map(pm => (
                    <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Budget Distribution</Label>
              <Select 
                value={formData.budget_distribution} 
                onValueChange={(v) => setFormData({...formData, budget_distribution: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {budgetDistributions.map(bd => (
                    <SelectItem key={bd.value} value={bd.value}>{bd.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Scheduling Type</Label>
              <Select 
                value={formData.scheduling_type} 
                onValueChange={(v) => setFormData({...formData, scheduling_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {schedulingTypes.map(st => (
                    <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData({...formData, status: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <Label>Statistical Project</Label>
                <Switch
                  checked={formData.is_statistical}
                  onCheckedChange={(v) => setFormData({...formData, is_statistical: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Allow Actual Postings</Label>
                <Switch
                  checked={formData.allow_actual_postings}
                  onCheckedChange={(v) => setFormData({...formData, allow_actual_postings: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Allow Commitments</Label>
                <Switch
                  checked={formData.allow_commitment_postings}
                  onCheckedChange={(v) => setFormData({...formData, allow_commitment_postings: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Allow Revenue</Label>
                <Switch
                  checked={formData.allow_revenue_postings}
                  onCheckedChange={(v) => setFormData({...formData, allow_revenue_postings: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Allow Networks</Label>
                <Switch
                  checked={formData.allow_network}
                  onCheckedChange={(v) => setFormData({...formData, allow_network: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Allow Milestones</Label>
                <Switch
                  checked={formData.allow_milestones}
                  onCheckedChange={(v) => setFormData({...formData, allow_milestones: v})}
                />
              </div>
            </div>
            
            {/* Scheduling Settings */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-orange-600 border-b pb-1">Scheduling Settings</h4>
              <div className="flex items-center justify-between">
                <div>
                  <Label>WBS Scheduling</Label>
                  <p className="text-xs text-gray-500">Enable date scheduling for WBS elements</p>
                </div>
                <Switch
                  checked={formData.wbs_scheduling}
                  onCheckedChange={(v) => setFormData({...formData, wbs_scheduling: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Network Scheduling</Label>
                  <p className="text-xs text-gray-500">Enable scheduling for networks and activities</p>
                </div>
                <Switch
                  checked={formData.network_scheduling}
                  onCheckedChange={(v) => setFormData({...formData, network_scheduling: v})}
                />
              </div>
            </div>
            
            {/* Settlement Settings */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-orange-600 border-b pb-1">Settlement Settings</h4>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Settlement Profile</Label>
                  <p className="text-xs text-gray-500">Enable cost settlement to receivers</p>
                </div>
                <Switch
                  checked={formData.settlement_profile}
                  onCheckedChange={(v) => setFormData({...formData, settlement_profile: v})}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-btn">
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default ProjectProfiles;
